import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { LogLevelEnum } from '@infrastructure/logLevel.enum';
import { FMResponseModel, LimitedEditionFieldDataModel } from '@models/api-models/FMResponseModel';
import { OrderCreateModel } from '@models/api-models/order-create-model';
import { OrderItemsCreateRequestModel } from '@models/api-models/request-models/orderItemsCreateRequestModel';
import { CustomerModel, CustomerOrderRelevantModel } from '@models/customer';
import { Order } from '@models/order';
import { OrderCompleteModel } from '@models/order-complete-model';
import { IOrderConfirmationEmail } from '@models/order-confirmation-email';
import { OrderItemModel } from '@models/order-item';
import { OrderItemMiscModel } from '@models/order-item-misc-model';
import { OrderItemState } from '@models/order-item-state';
import { OrderItemType } from '@models/order-item-type';
import { OrderState } from '@models/order-state';
import { OrderUpdateModel } from '@models/order-update-model';
import { OrderIdsModel } from '@models/orderIds-model';
import { OrdersModel } from '@models/orders';
import { PriceItem, PriceResponse } from '@models/price-model';
import { TempleInclinationEnum } from '@models/variant-temple-inclination';
import { AppState } from '@shared/app-state';
import { Mutex, MutexInterface } from 'async-mutex';
import { plainToClass } from 'class-transformer';
import { Subject } from 'rxjs';
import { CustomerDataService } from './customer-data.service';
import { DataApiService } from './data-api.service';
import { HttpService } from './http.service';
import { LogEvent, LoggerService } from './logger.service';
import { OrderItemService } from './order-item.service';
import { SessionService } from './session.service';
import { ModelsService } from './v2/models.service';

@Injectable({
	providedIn: 'root'
})
export class OrdersDataService {

	public orderDataChanged = new Subject<string | null>();
	public priceDataChanged = new Subject();
	public orderItemDataChanged = new Subject<OrderItemModel>();
	public orderItemMiscDataChanged = new Subject<OrderItemMiscModel>();
	public orderItemPriceLookup = new Subject<PriceItem>();
	public orderItemIsDoingPriceLookup = new Subject<boolean>();
	public orderItemMiscPriceLookup = new Subject<PriceItem>();
	public orderItemMiscIsDoingPriceLookup = new Subject<boolean>();

	/*
	* IMPORTAMT that we use mutex. GetOrders is getting called everytime a dirty state occurs (example is a item is deleted).
	* We have to make sure, we dont post anyting to an order (example orderitem on order), at the same time we is getting the order (deadlock chance).
	*/
	private ordersDataMutex = new Mutex();
	private mutexReleaser: MutexInterface.Releaser;

	constructor(
		private appState: AppState,
		private dataApiService: DataApiService,
		private customerDataService: CustomerDataService,
		private sessionService: SessionService,
		private httpService: HttpService,
		private loggerService: LoggerService,
		private orderItemService: OrderItemService,
		private modelsService: ModelsService
	) { }

	public async createOrderMiscItem(orderItemMiscModel: OrderItemMiscModel, skipOrderReload: boolean = false): Promise<void> {
		await this.postMiscOrderItem(orderItemMiscModel, skipOrderReload);
	}

	public async createOrderItem(orderItemCreateModel: OrderItemState, skipOrderReload: boolean = false): Promise<OrderItemState> {
		await this.postOrderItem(orderItemCreateModel, skipOrderReload);

		return orderItemCreateModel;
	}

	public async createOrderItems(orderItemStates: OrderItemState[], skipOrderReload: boolean = false): Promise<OrderItemState[]> {
		await this.postOrderItems(orderItemStates, skipOrderReload);

		return orderItemStates;
	}

	public async createOrderItemsByCollectionId(requestModel: OrderItemsCreateRequestModel): Promise<boolean> {
		const result = await this.postOrderItemsByCollectionId(requestModel);

		return result;
	}

	public async editOrderMiscItem(orderItem: OrderItemMiscModel): Promise<void> {
		const result = await this.orderItemService.editOrderMiscItem(orderItem);
		this.orderItemMiscDataChanged.next(orderItem);
		this.reloadOrders(orderItem.CustomerNo) // non blocking on purpose

		return result;
	}

	public async editOrderItem(orderItem: OrderItemModel): Promise<OrderItemModel> {
		await this.lowTempleInclinationCheckOnEdit(orderItem);

		const orderItemModel = await this.orderItemService.editOrderItem(orderItem);
		this.orderItemDataChanged.next(orderItem);
		this.reloadOrders(orderItem.CustomerNo) // non blocking on purpose

		return orderItemModel;
	}

	public async moveOrderMiscItem(Uuid: string, orderId: number, customerNo?: string, skipOrderReload: boolean = false) {
		await this.orderItemService.moveOrderMiscItem(Uuid, orderId);

		if (!skipOrderReload) {
			this.reloadOrders(customerNo) // non blocking on purpose
		}
	}

	public async moveOrderItem(Uuid: string, orderId: number, customerNo?: string, skipOrderReload: boolean = false) {
		await this.orderItemService.moveOrderItem(Uuid, orderId);

		if (!skipOrderReload) {
			this.reloadOrders(customerNo) // non blocking on purpose
		}
	}

	public async deleteOrderMiscItem(uuid: string, customerNo: string, skipOrderReload: boolean = false): Promise<void> {
		const result = await this.dataApiService.deleteOrderMiscItem(uuid);

		if (!skipOrderReload) {
			this.reloadOrders(customerNo) // non blocking on purpose
		}

		return result;
	}

	public async deleteOrderItem(uuid: string, customerNo: string, skipOrderReload: boolean = false): Promise<void> {
		const result = await this.orderItemService.deleteOrderItem(uuid);

		if (!skipOrderReload) {
			this.reloadOrders(customerNo) // non blocking on purpose
		}

		return result;
	}

	public async editOrderData(orderId: number, model: OrderUpdateModel, customerNo: string): Promise<void> {
		await this.dataApiService.setOrderData(orderId, model);

		this.reloadOrders(customerNo) // non blocking on purpose
	}

	public async deleteOrder(id: number | undefined, customerNo: string): Promise<void> {
		const result = await this.dataApiService.deleteOrder(id);

		this.reloadOrders(customerNo) // non blocking on purpose

		return result;
	}

	public async requestOrder(orders: Array<OrderCompleteModel>, customerNo: string, IsNewPartner: boolean | undefined): Promise<void> {
		await this.dataApiService.requestOrder(orders, IsNewPartner);

		this.reloadOrders(customerNo) // non blocking on purposes
	}

	public async completeOrder(orders: Array<OrderCompleteModel>, customerNo: string): Promise<void> {
		await this.dataApiService.completeOrder(orders);

		this.reloadOrders(customerNo) // non blocking on purpose
	}

	public async createNewOrder(order: OrderCreateModel, customerNo: string | undefined | null, skipOrderReload: boolean = false, isNewPartner = false): Promise<number> {
		const result = await this.dataApiService.createNewOrder(order, isNewPartner);

		//vi skal lave skip reload Option. Skal bruges i flere tilfælde!!!!
		if (!skipOrderReload) {
			this.reloadOrders(customerNo) // non blocking on purpose
		}

		return result;
	}

	public async postOrderItem(orderItemCreateModel: OrderItemState, skipOrderReload: boolean = false): Promise<OrderItemState> {
		await this.lowTempleInclinationCheckOnCreate(orderItemCreateModel);

		const result = await this.orderItemService.postItem(orderItemCreateModel);

		if (!skipOrderReload) {
			this.reloadOrders(orderItemCreateModel.CustomerNo) // non blocking on purpose
		}

		return result;
	}

	public async postOrderItems(orderItemCreateModel: OrderItemState[], skipOrderReload: boolean = false): Promise<OrderItemState[]> {
		this.mutexReleaser = await this.ordersDataMutex.acquire();

		try {
			// await this.lowTempleInclinationCheckOnCreate(orderItemCreateModel);

			const result = await this.orderItemService.postItems(orderItemCreateModel);

			if (!skipOrderReload) {
				this.reloadOrders(orderItemCreateModel[0].CustomerNo) // non blocking on purpose
			}

			return result;
		}
		finally {
			this.mutexReleaser();
		}
	}

	public async postOrderItemsByCollectionId(requestModel: OrderItemsCreateRequestModel): Promise<boolean> {
		this.mutexReleaser = await this.ordersDataMutex.acquire();

		try {
			const result = await this.orderItemService.postItemsByCollectionId(requestModel);
			return result;
		}
		finally {
			this.mutexReleaser();
		}
	}

	public async postMiscOrderItem(orderItemMiscModel: OrderItemMiscModel, skipOrderReload: boolean = false) {
		this.mutexReleaser = await this.ordersDataMutex.acquire();

		try {
			const result = await this.dataApiService.postMiscItem(orderItemMiscModel);

			if (!skipOrderReload) {
				this.reloadOrders(orderItemMiscModel.CustomerNo) // non blocking on purpose
			}

			return result;
		}
		finally {
			this.mutexReleaser();
		}
	}

	private async lowTempleInclinationCheckOnCreate(orderItemCreateModel: OrderItemState) {
		if (orderItemCreateModel.LowTempleInclinationPossible !== undefined
			&& !orderItemCreateModel.LowTempleInclinationPossible
			&& orderItemCreateModel.TempleInclination === TempleInclinationEnum.low) {
			await this.logInvalidTempleInclination(orderItemCreateModel, orderItemCreateModel.OrderId, 'onCreate')
			orderItemCreateModel.TempleInclination = TempleInclinationEnum.standard;
		}
	}

	private async lowTempleInclinationCheckOnEdit(orderItem: OrderItemModel) {
		if (orderItem.LowTempleInclinationPossible !== undefined
			&& !orderItem.LowTempleInclinationPossible
			&& orderItem.TempleInclination === TempleInclinationEnum.low) {
			await this.logInvalidTempleInclination(orderItem, orderItem.OrderId, 'onEdit')
			orderItem.TempleInclination = TempleInclinationEnum.standard;
		}
	}

	private async logInvalidTempleInclination(orderItem: OrderItemState | OrderItemModel, orderId: number | undefined | null, context: string) {
		let logEvent = new LogEvent(
			`OrderItem contained invalid temple inclination (${context})`,
			LogLevelEnum.Warning,
			{
				'orderItem': JSON.stringify(orderItem),
				'orderId': `OrderId [${orderId}]`
			}
		);

		this.loggerService.sendClientLog(logEvent);
	}

	public async getCustomerOrderRelevantData(customerNo: string): Promise<CustomerOrderRelevantModel> {
		const orderOnUser = await this.dataApiService.getOrders(customerNo); //await this.getFromOrdersStore(customerNo);

		let orderRelevantData: CustomerOrderRelevantModel;

		if (orderOnUser) {
			orderRelevantData = new CustomerOrderRelevantModel(orderOnUser);
		}
		else {
			orderRelevantData = await this.getCustomerOrderRelevantData(customerNo);
		}

		return orderRelevantData;
	}

	public async getOrderIds(customerNo?: string | null): Promise<Array<OrderIdsModel> | null> {
		const orders = await this.dataApiService.getOrders(customerNo); //await this.getFromOrdersStore(customerNo);

		let orderIdsModel: Array<OrderIdsModel> | null = [];

		if (orders) {
			orderIdsModel = orders.map(o => new OrderIdsModel(o));
		}
		else {
			const crmTicketId = this.appState.afterSalesCRMCaseNo.value;
			orderIdsModel = await this.dataApiService.getOrderIds(customerNo, crmTicketId);
		}

		return orderIdsModel;
	}

	public async getOrdersData(customerNo?: string | null): Promise<Array<OrderState>> {
		this.mutexReleaser = await this.ordersDataMutex.acquire();

		try {
			const orders = await this.dataApiService.getOrders(customerNo); // await this.getFromOrdersStore<Array<OrderState>>(customerNo);
			return orders;
		}
		finally {
			this.mutexReleaser();
		}
	}

	public async getOrders(customerNo: string | undefined, customer: CustomerModel | null, deliveryEmails?: string): Promise<OrdersModel> {
		let orders: OrderState[];
		orders = await this.dataApiService.getOrders(customerNo);

		if (orders.length > 0) {
			var isConsultant = customer != null;

			if (isConsultant && deliveryEmails && deliveryEmails.indexOf(',') > 0) {
				deliveryEmails = deliveryEmails.split(',')[0];
			}

			let emailAddresses: string[] = [];
			if (deliveryEmails) {
				emailAddresses = deliveryEmails.indexOf(',') > 0 ? deliveryEmails.split(',')
					: [deliveryEmails];
			}

			if (customer && customer.EmailAddresses) {
				for (let index = 0; index < customer.EmailAddresses.length; index++) {
					const customerEmailAddress = customer.EmailAddresses[index];

					if (emailAddresses.indexOf(customerEmailAddress) < 0) {
						emailAddresses.push(customerEmailAddress);
					}
				}
			}

			for (let index = 0; index < orders.length; index++) {
				const order = orders[index];

				let emailAddressesOnOrder = order?.CheckedOrderConfirmationEmailAddresses;

				const allEmails = Array.from(new Set([
					...emailAddresses,
					...(emailAddressesOnOrder?.filter(e => !!e) ?? [])
				]));

				let confirmationEmails = allEmails.map(x => ({ Email: x, Checked: true }));

				// let emailAddressesOnOrder = order?.CheckedOrderConfirmationEmailAddresses;

				// let confirmationEmails: Array<IOrderConfirmationEmail> = emailAddresses.map(x =>
				// 	({ Email: x, Checked: emailAddressesOnOrder?.some(t => t == x) })
				// );

				// const allEmails = Array.from(new Set([
				// 	...emailAddresses,
				// 	...(emailAddressesOnOrder?.filter(e => !!e) ?? [])
				// ]));

				// if (!emailAddressesOnOrder) {
				// 	// No previous selection -> default all emails (from both sources) to checked
				// 	confirmationEmails = allEmails.map(x => ({ Email: x, Checked: true }));
				// }
				// else if (emailAddressesOnOrder[0] === '') {
				// 	// Special flag: all previously unchecked -> include all emails but unchecked
				// 	confirmationEmails = allEmails.map(x => ({ Email: x, Checked: false }));
				// }
				// else {
				// 	// Respect previous selections: include all emails; check only those previously selected
				// 	confirmationEmails = allEmails.map(x => ({ Email: x, Checked: emailAddressesOnOrder?.some(t => t === x) }));
				// }

				order.ConfirmationEmails = confirmationEmails;
				order.CheckedOrderConfirmationEmailAddresses = confirmationEmails?.filter(t => t.Checked).map(c => c.Email);
				order.CustomerEmail = order?.CheckedOrderConfirmationEmailAddresses?.filter(e => e).join(',')
			}
		}

		const miscItems = await this.modelsService.getMiscModels(true);

		for (let index = 0; index < orders.length; index++) {
			let currentOrder = orders[index];

			currentOrder.OrderItemMiscs.forEach(y => {
				const misc = miscItems.find(item => item.EcommerceNoUnique == y.EcommerceNoUnique);
				if (misc)
					y.MiscModel = misc;
			})

			const salesPersonsCustomers = (await this.customerDataService.getCustomersData(true)).filter(c => c.No === currentOrder.CustomerNo);

			if (salesPersonsCustomers.length) {
				currentOrder.SalesDistrictType = salesPersonsCustomers[0].SalesDistrictType;
			}
		}

		const orderResult = orders.map(x => new Order(plainToClass(OrderState, x)));

		const orderitemsCollapsedMap = this.sessionService.get<Array<{ uuid: string, collapsed: boolean }>>('orderitemsCollapsedMap');
		const isOrderingForCustomer = customer != null;

		orderResult.forEach(order => {
			order.OrderItems.forEach(bagItem => {
				if (bagItem.getState() != null) {
					const element = orderitemsCollapsedMap?.find(x => x.uuid == bagItem.Uuid);

					if (element?.uuid) {
						bagItem.Collapsed = element.collapsed;
					}
					else if (bagItem.IsFullFrame && isOrderingForCustomer && !(bagItem.Engraving || bagItem.Reference
						|| bagItem.Comment || bagItem.containsDiscount() || bagItem.HasForms || bagItem.HasFixtures)) {

						bagItem.Collapsed = true;
					}

					if (!bagItem.Price && bagItem.PriceJson) {
						const priceResponse: PriceItem = new PriceItem(bagItem.PriceJson);

						if (priceResponse) {
							priceResponse.isDoingPriceQuery = false;
							bagItem.Price = priceResponse;
						}
					}
				}
			});

			order.OrderItemMiscs.forEach(miscItem => {
				if (!miscItem.Price && miscItem.PriceJson) {
					const priceResponse: PriceItem = new PriceItem(miscItem.PriceJson);
					priceResponse.isDoingPriceQuery = false;
					miscItem.Price = priceResponse;
				}
			});
		});

		return new OrdersModel(this.appState, orderResult);
	}

	public async reloadOrders(customerNo?: string | null) {
		this.orderDataChanged.next(customerNo ?? null);

		const orders = await this.getOrdersData(customerNo);

		return orders;
	}

	public async updatePrice(frameOrderItem: OrderItemModel | null, orderItemMisc: OrderItemMiscModel | null, saveOrderItem: boolean = true): Promise<PriceResponse | null> {
		let priceResponse: PriceResponse | null = null

		if (frameOrderItem) {
			priceResponse = (await this.dataApiService.getPrice(OrderItemType.OrderItem, frameOrderItem.Uuid));
			let priceItem = new PriceItem(JSON.stringify(priceResponse.data?.responseItems[0]));
			frameOrderItem.Price = priceItem;

			if (frameOrderItem.Price?.priceFound) {
				if (saveOrderItem) {
					await this.orderItemService.editOrderItem(frameOrderItem);
					await this.reloadOrders(frameOrderItem.CustomerNo) // non blocking on purpose
				}
				await this.priceDataChanged.next(frameOrderItem.Price);
			}
		}
		else if (orderItemMisc) {
			priceResponse = (await this.dataApiService.getPrice(OrderItemType.OrderItemMisc, orderItemMisc.Uuid));
			let priceItem = new PriceItem(JSON.stringify(priceResponse.data?.responseItems[0]));
			orderItemMisc.Price = priceItem;

			if (orderItemMisc.Price?.priceFound) {
				orderItemMisc.Price.lookupTimestamp = new Date();
				if (saveOrderItem) {
					await this.orderItemService.editOrderMiscItem(orderItemMisc);
					await this.reloadOrders(orderItemMisc.CustomerNo) // non blocking on purpose
				}
				await this.priceDataChanged.next(orderItemMisc.Price);
			}
		}

		return priceResponse;
	}

	async getLimitedEditionData() {
		let result: FMResponseModel<LimitedEditionFieldDataModel> = await this.httpService.getDataWithOfflineCheck<FMResponseModel<LimitedEditionFieldDataModel>>(`${environment.dataUrl}/limitededitionprecioustemple`);
		return result;
	}

}
